import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Steps } from './WorkspaceName';
import Confetti from '../../components/Celebrations/Confetti';
import {
  CheckCircle,
  Globe,
  Link as LinkIcon,
  ChartBar,
  Lightning,
  Users,
  BookOpen,
  ArrowRight,
} from '@phosphor-icons/react';

const CHECKLIST_ITEMS = [
  { key: 'connected_domain', icon: Globe, label: 'Connect a domain', hint: 'Add a custom or free subdomain' },
  { key: 'first_link_created', icon: LinkIcon, label: 'Create your first link', hint: 'Shorten any URL' },
  { key: 'viewed_analytics', icon: ChartBar, label: 'Explore analytics', hint: 'See clicks and geography' },
  { key: 'viewed_events', icon: Lightning, label: 'Explore events', hint: 'Real-time click stream' },
  { key: 'invited_member', icon: Users, label: 'Invite a team member', hint: 'Collaborate in one workspace' },
  { key: 'viewed_docs', icon: BookOpen, label: 'Read the docs', hint: 'Learn advanced features' },
];

export default function WorkspaceReady() {
  const { workspace, setWorkspace } = useAuth();
  const navigate = useNavigate();
  const [confetti, setConfetti] = useState(true);

  // Tick onboarding step as workspace is ready
  useEffect(() => {
    if (!workspace?.workspace_id) return;
    const current = workspace?.onboarding || {};
    if (current.completed_at) return;

    const allDone = CHECKLIST_ITEMS.every((i) => current[i.key]);
    if (allDone) {
      supabase
        .from('workspaces')
        .update({
          onboarding: { ...current, completed_at: new Date().toISOString() },
        })
        .eq('workspace_id', workspace.workspace_id)
        .select()
        .single()
        .then(({ data }) => { if (data) setWorkspace(data); });
    }
  }, [workspace, setWorkspace]);

  const onboarding = workspace?.onboarding || {};
  const doneCount = CHECKLIST_ITEMS.filter((i) => onboarding[i.key]).length;
  const pct = Math.round((doneCount / CHECKLIST_ITEMS.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
      <Confetti active={confetti} duration={3500} />

      <div className="absolute top-8 left-1/2 -translate-x-1/2">
        <Steps current={2} />
      </div>

      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-3xl font-extrabold text-foreground mb-2">
            Your workspace is ready!
          </h1>
          <p className="text-muted-foreground">
            Complete these steps to get the most out of LinkFlow.
          </p>
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-2xl border border-border shadow-soft p-6 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-foreground">Getting started</span>
            <span className="text-sm font-bold text-primary">{doneCount}/{CHECKLIST_ITEMS.length}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden mb-5">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="space-y-2.5">
            {CHECKLIST_ITEMS.map(({ key, icon: Icon, label, hint }) => {
              const done = !!onboarding[key];
              return (
                <div
                  key={key}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    done
                      ? 'bg-emerald-50/60 border-emerald-200/60'
                      : 'bg-white border-border hover:border-primary/30 cursor-pointer'
                  }`}
                  onClick={() => !done && getAction(key, navigate)}
                >
                  <div
                    className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                      done ? 'bg-emerald-100 text-emerald-600' : 'bg-primary/10 text-primary'
                    }`}
                  >
                    {done ? <CheckCircle size={18} weight="fill" /> : <Icon size={18} weight="duotone" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {label}
                    </p>
                    <p className="text-xs text-muted-foreground">{hint}</p>
                  </div>
                  {!done && (
                    <ArrowRight size={14} className="text-muted-foreground shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Help', href: 'https://docs.linkflow.app', emoji: '📚' },
            { label: 'Docs', href: 'https://docs.linkflow.app', emoji: '📖' },
            { label: 'Support', href: 'mailto:support@linkflow.app', emoji: '💬' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border bg-white hover:border-primary/30 hover:bg-primary/5 transition-all text-center"
            >
              <span className="text-xl">{item.emoji}</span>
              <span className="text-xs font-semibold text-foreground">{item.label}</span>
            </a>
          ))}
        </div>

        <button
          onClick={() => {
            setConfetti(false);
            navigate('/dashboard');
          }}
          className="w-full h-12 rounded-xl gradient-primary text-white font-semibold text-base flex items-center justify-center gap-2 shadow-glow hover:opacity-95 transition"
          data-testid="go-to-dashboard-btn"
        >
          Go to Dashboard
          <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function getAction(key, navigate) {
  const map = {
    connected_domain: () => navigate('/domains'),
    first_link_created: () => navigate('/links?new=1'),
    viewed_analytics: () => navigate('/dashboard'),
    viewed_events: () => navigate('/events'),
    invited_member: () => navigate('/settings'),
    viewed_docs: () => window.open('https://docs.linkflow.app', '_blank'),
  };
  map[key]?.();
}
